// client/src/pages/OperationsConsole.tsx
//
// Operations — every scheduled refresh the firm runs, in one place: what each
// one writes, the rule that triggers it, its rails, its cadence and window, its
// run history, and the switches. Backed by api/ops-console over ops.Job /
// JobRun / Heartbeat / Setting / ConfigAudit (master-hub migration 013). The
// policy behind each row is master-hub governance/REFRESH-POLICY.md.
//
// THE TIMER IS A HEARTBEAT. func-shared-services ticks once a minute and runs
// what these rows say is due, so editing a row here IS the schedule change.
// A heartbeat that goes quiet means nothing is running at all, which is why it
// sits at the top of the page and turns red after three minutes.
//
// NOT EVERY ROW IS RUNNABLE. Jobs whose builder hasn't moved into the function
// app yet (HandlerStatus not-ported / planned) are listed with what blocks
// them, so the policy is visible in full, but the API refuses to turn them on.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Timer, RefreshCw, Play, HeartPulse, History, Settings2, Users, ScrollText,
  CheckCircle2, XCircle, MinusCircle, AlertTriangle, ChevronDown, ChevronRight, Lock,
} from 'lucide-react';

const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b', idle: '#7b8794' };

type Job = {
  JobKey: string; App: string; Title: string; Description: string | null;
  Layer: string | null; Source: string | null; TriggerRule: string | null; FreshnessTarget: string | null;
  Rails: string | null; Writer: string | null; BlockedOn: string | null;
  HandlerStatus: 'ready' | 'planned' | 'not-ported' | 'observed';
  Enabled: boolean; CadenceMinutes: number | null; WindowStartUtc: string | null; WindowEndUtc: string | null;
  WeekdaysOnly: boolean; Priority: number; TimeoutSeconds: number; ParamsJson: string | null;
  AlertRecipients: string | null; AlertCooldownMinutes: number;
  RunRequestedAt: string | null; RunRequestedBy: string | null; UpdatedAt: string | null; UpdatedBy: string | null;
  LastStartedAt: string | null; LastFinishedAt: string | null; LastStatus: string | null; LastError: string | null;
  LastSummaryJson: string | null; ConsecutiveFailures: number; LastAlertAt: string | null;
  Runs7d: number; Failures7d: number; Next: { at: string | null; why: string }; Overdue: boolean;
};
type Beat = { App: string; LastTickAt: string; LastTickMs: number | null; Schedule: string | null; HostInstance: string | null; AgeSeconds: number; Stale: boolean;
  Uptime: { ticks: number; expected: number; percent: number; longestGapSeconds: number | null; gaps: number } | null };
type Setting = { SettingKey: string; Value: string; Description: string | null; UpdatedAt: string | null; UpdatedBy: string | null };
type Space = { Upn: string; IntakeEnabled: boolean; Proposals: number };
type Overview = { jobs: Job[]; heartbeats: Beat[]; settings: Setting[]; personalSpaces: Space[]; staleAfterMinutes: number; asOf: string };
type Run = { RunId: number; JobKey: string; TriggerKind: string; TriggeredBy: string | null; StartedAt: string; FinishedAt: string | null;
  DurationMs: number | null; Status: string; ItemsProcessed: number | null; SummaryJson: string | null; Error: string | null; HostInstance: string | null };
type Audit = { AuditId: number; Entity: string; EntityKey: string; Field: string | null; OldValue: string | null; NewValue: string | null; ChangedBy: string; ChangedAt: string };

const RUNNER_APP = 'func-shared-services';

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const m = ms / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)}m ago`;
  if (m < 36 * 60) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
}

function until(iso: string | null): string {
  if (!iso) return '';
  const m = (Date.parse(iso) - Date.now()) / 60000;
  if (m < 1.5) return 'within a minute';
  if (m < 60) return `in ${Math.round(m)}m`;
  if (m < 36 * 60) return `in ${Math.round(m / 60)}h`;
  return `in ${Math.round(m / 1440)}d`;
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

/** "14:00" UTC → "9:00 AM" in Central, for today's offset. */
function centralOf(hhmm: string | null): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
}

function cadenceText(j: Job): string {
  if (j.HandlerStatus === 'observed') return j.TriggerRule || 'set in code';
  if (!j.CadenceMinutes) return j.TriggerRule || 'not on a clock';
  const c = j.CadenceMinutes;
  const every = c % 10080 === 0 ? `every ${c / 10080 === 1 ? 'week' : `${c / 10080} weeks`}`
    : c % 1440 === 0 ? (c === 1440 ? 'daily' : `every ${c / 1440} days`)
    : c % 60 === 0 ? `every ${c / 60}h` : `every ${c} min`;
  return every;
}

function windowText(j: Job): string | null {
  const bits: string[] = [];
  if (j.WindowStartUtc && j.WindowEndUtc) bits.push(`${centralOf(j.WindowStartUtc)}–${centralOf(j.WindowEndUtc)} CT`);
  if (j.WeekdaysOnly) bits.push('weekdays');
  return bits.length ? bits.join(' · ') : null;
}

function StatePill({ j }: { j: Job }) {
  const map: Record<string, [string, string]> = {
    on: ['On', STATUS.good], off: ['Off', STATUS.idle],
    'not-ported': ['Needs porting', STATUS.warning], planned: ['Not built', STATUS.idle], observed: ['In code', STATUS.idle],
  };
  if (j.Overdue) {
    return (
      <span title="On, but it hasn't started for more than twice its cadence"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: STATUS.critical,
          border: `1px solid ${STATUS.critical}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>
        <AlertTriangle size={11} />Overdue
      </span>
    );
  }
  const k = j.HandlerStatus === 'ready' ? (j.Enabled ? 'on' : 'off') : j.HandlerStatus;
  const [label, color] = map[k];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color,
      border: `1px solid ${color}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>
      {k !== 'on' && k !== 'off' && <Lock size={11} />}{label}
    </span>
  );
}

function RunStatus({ status, failures }: { status: string | null; failures?: number }) {
  if (!status) return <span className="muted">never run</span>;
  const color = status === 'succeeded' ? STATUS.good : status === 'running' ? STATUS.warning
    : status === 'failed' || status === 'timed-out' ? STATUS.critical : STATUS.idle;
  const Icon = status === 'succeeded' ? CheckCircle2 : status === 'failed' || status === 'timed-out' ? XCircle : MinusCircle;
  return (
    <span style={{ color, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <Icon size={14} />{status}{failures ? <span style={{ fontSize: 11 }}>({failures} in a row)</span> : null}
    </span>
  );
}

function Tile({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: string; accent?: string }) {
  return (
    <div className="card" style={{ padding: '12px 18px', minWidth: 150, borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: accent || 'var(--text)' }}>{value}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2, opacity: 0.75 }}>{sub}</div>}
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textAlign: 'left', fontSize: 12 };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
const label: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--muted)', marginBottom: 3 };

function Fact({ k, v }: { k: string; v: React.ReactNode }) {
  if (v == null || v === '') return null;
  return <div><div style={label}>{k}</div><div style={{ fontSize: 13, lineHeight: 1.5 }}>{v}</div></div>;
}

const parse = (json: string | null): Record<string, unknown> | null => {
  if (!json) return null;
  try { return JSON.parse(json); } catch { return null; }
};

/** One line: the counts. Nested backlog is flattened; lists become a count. */
function Summary({ json }: { json: string | null }) {
  const o = parse(json);
  if (!o) return json ? <code style={{ fontSize: 12 }}>{json}</code> : null;
  const bits: string[] = [];
  for (const [k, v] of Object.entries(o)) {
    if (Array.isArray(v)) continue;
    if (v && typeof v === 'object') {
      if (k === 'backlog') bits.push(`backlog ${Object.entries(v as Record<string, unknown>).filter(([, x]) => x != null).map(([a, x]) => `${a} ${x}`).join(', ')}`);
      continue;
    }
    bits.push(`${k} ${String(v)}`);
  }
  return <span style={{ fontSize: 12 }}>{bits.join(' · ')}</span>;
}

type Gap = { code: string; client: string; job: string; year: number; people: string[]; missing: string[] };

/**
 * What the last run found, in the shape that says whether the job is doing its
 * job: the backlog it exists to keep at zero, per-person results, and for
 * two-year coverage, every project still missing a year.
 */
function LatestResult({ j }: { j: Job }) {
  const o = parse(j.LastSummaryJson);
  if (!o) return null;
  const backlog = o.backlog as Record<string, number | null> | undefined;
  const detail = o.detail as Array<Record<string, unknown>> | undefined;
  const gaps = o.gaps as Gap[] | undefined;
  const byKind = o.byKind as Record<string, { projects: number; complete: number; missing: Record<string, number> }> | undefined;
  const waiting = backlog ? Object.entries(backlog).some(([k, v]) => k !== 'oldestMinutes' && Number(v) > 0) : false;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={label}>Latest result · {ago(j.LastFinishedAt)}</div>
      {backlog && (
        <div style={{ fontSize: 13, marginBottom: 8, color: waiting ? STATUS.warning : undefined }}>
          <strong>Backlog:</strong>{' '}
          {Object.entries(backlog).filter(([k]) => k !== 'oldestMinutes').map(([k, v]) => `${k} ${v ?? 0}`).join(' · ')}
          {backlog.oldestMinutes != null && ` · oldest waiting ${backlog.oldestMinutes} min`}
          {!waiting && <span className="muted"> (clear)</span>}
        </div>
      )}
      {byKind && (
        <div style={{ fontSize: 13, marginBottom: 8, display: 'grid', gap: 3 }}>
          <div><strong>{String(o.complete)} of {String(o.projects)}</strong> live projects have both years on file
            ({String(o.percent ?? '—')}%), across {String(o.people)} people{Number(o.notInLibrary) ? `; ${o.notInLibrary} clients not in the library` : ''}.</div>
          {Object.entries(byKind).map(([kind, k]) => (
            <div key={kind} className="muted" style={{ fontSize: 12 }}>
              {kind.replace(/-/g, ' ')}: {k.complete}/{k.projects} complete
              {Object.keys(k.missing).length > 0 && ` · missing ${Object.entries(k.missing).map(([m, n]) => `${m} ${n}`).join(', ')}`}
            </div>
          ))}
        </div>
      )}
      {detail && detail.length > 0 && (
        <div style={{ fontSize: 12.5, display: 'grid', gap: 2, marginBottom: 8 }}>
          {detail.map((d) => (
            <div key={String(d.who)}>
              <span style={{ fontWeight: 600 }}>{String(d.who)}</span>{' '}
              {d.error ? <span style={{ color: STATUS.critical }}>{String(d.error)}</span>
                : <span className="muted">{Object.entries(d).filter(([k, v]) => k !== 'who' && v != null).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>}
            </div>
          ))}
        </div>
      )}
      {gaps && gaps.length > 0 && (
        <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Client', 'Project', 'Who', 'Missing'].map((h) => <th key={h} style={{ ...th, position: 'sticky', top: 0, background: 'var(--bg)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {gaps.map((g) => (
                <tr key={`${g.code}-${g.job}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...td, padding: '5px 10px' }}><code>{g.code}</code> {g.client}</td>
                  <td style={{ ...td, padding: '5px 10px' }}>{g.job}</td>
                  <td style={{ ...td, padding: '5px 10px' }} className="muted">{g.people.join(', ')}</td>
                  <td style={{ ...td, padding: '5px 10px', color: STATUS.warning }}>{g.missing.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type Draft = Partial<Record<'Enabled' | 'CadenceMinutes' | 'WindowStartUtc' | 'WindowEndUtc' | 'WeekdaysOnly' | 'TimeoutSeconds' | 'AlertRecipients' | 'AlertCooldownMinutes' | 'ParamsJson', string | boolean>>;

function JobDetail({ j, runs, onSave, onRun, busy }: {
  j: Job; runs: Run[]; busy: boolean;
  onSave: (d: Draft) => void; onRun: () => void;
}) {
  const ready = j.HandlerStatus === 'ready';
  const initial: Draft = {
    Enabled: j.Enabled, CadenceMinutes: j.CadenceMinutes == null ? '' : String(j.CadenceMinutes),
    WindowStartUtc: j.WindowStartUtc || '', WindowEndUtc: j.WindowEndUtc || '', WeekdaysOnly: j.WeekdaysOnly,
    TimeoutSeconds: String(j.TimeoutSeconds), AlertRecipients: j.AlertRecipients || '',
    AlertCooldownMinutes: String(j.AlertCooldownMinutes), ParamsJson: j.ParamsJson || '',
  };
  const [d, setD] = useState<Draft>(initial);
  useEffect(() => { setD(initial); }, [j.UpdatedAt, j.JobKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = Object.keys(initial).some((k) => (initial as Record<string, unknown>)[k] !== (d as Record<string, unknown>)[k]);
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setD({ ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const id = (k: string) => `ops-${j.JobKey}-${k}`;

  return (
    <div style={{ padding: '14px 16px 18px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
      {j.Description && <div style={{ fontSize: 13, marginBottom: 12, maxWidth: 820 }}>{j.Description}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px 22px', marginBottom: 14 }}>
        <Fact k="Writes" v={j.Layer} />
        <Fact k="From" v={j.Source} />
        <Fact k="Trigger" v={j.TriggerRule} />
        <Fact k="Freshness target" v={j.FreshnessTarget} />
        <Fact k="Rails" v={j.Rails} />
        <Fact k="Sole writer" v={j.Writer} />
      </div>
      {j.BlockedOn && (
        <div className="card" style={{ borderLeft: `3px solid ${STATUS.warning}`, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
          <strong>What blocks it:</strong> {j.BlockedOn}
        </div>
      )}
      {j.LastError && (
        <div style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, color: STATUS.critical, marginBottom: 14, whiteSpace: 'pre-wrap' }}>
          Last error: {j.LastError}
        </div>
      )}

      <LatestResult j={j} />

      {j.HandlerStatus !== 'observed' && (
        <div className="card" style={{ padding: '12px 14px', marginBottom: 14, opacity: ready ? 1 : 0.6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Settings2 size={15} /><strong style={{ fontSize: 13 }}>Schedule</strong>
            {!ready && <span className="muted" style={{ fontSize: 12 }}>Locked until the job is in the scheduler.</span>}
            {j.UpdatedBy && <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>last changed {ago(j.UpdatedAt)} by {j.UpdatedBy}</span>}
          </div>
          <fieldset disabled={!ready || busy} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '10px 18px', alignItems: 'flex-end' }}>
            <label htmlFor={id('en')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input id={id('en')} type="checkbox" checked={!!d.Enabled} onChange={set('Enabled')} /> On
            </label>
            <div><div style={label}>Every (min)</div>
              <input id={id('cad')} type="number" min={1} value={String(d.CadenceMinutes ?? '')} onChange={set('CadenceMinutes')} style={{ width: 90 }} /></div>
            <div><div style={label}>Window start (UTC)</div>
              <input id={id('ws')} placeholder="HH:MM" value={String(d.WindowStartUtc ?? '')} onChange={set('WindowStartUtc')} style={{ width: 80 }} />
              <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{centralOf(String(d.WindowStartUtc || '') || null)}</span></div>
            <div><div style={label}>Window end (UTC)</div>
              <input id={id('we')} placeholder="HH:MM" value={String(d.WindowEndUtc ?? '')} onChange={set('WindowEndUtc')} style={{ width: 80 }} />
              <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{centralOf(String(d.WindowEndUtc || '') || null)}</span></div>
            <label htmlFor={id('wd')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input id={id('wd')} type="checkbox" checked={!!d.WeekdaysOnly} onChange={set('WeekdaysOnly')} /> Weekdays only
            </label>
            <div><div style={label}>Timeout (s)</div>
              <input id={id('to')} type="number" min={10} max={280} value={String(d.TimeoutSeconds ?? '')} onChange={set('TimeoutSeconds')} style={{ width: 80 }} /></div>
            <div style={{ flex: '1 1 240px' }}><div style={label}>Alert to (blank = dev inbox)</div>
              <input id={id('ar')} value={String(d.AlertRecipients ?? '')} onChange={set('AlertRecipients')} placeholder="DataAnalyticsTeam@bmss.com" style={{ width: '100%' }} /></div>
            <div><div style={label}>Alert at most every (min)</div>
              <input id={id('cd')} type="number" min={1} value={String(d.AlertCooldownMinutes ?? '')} onChange={set('AlertCooldownMinutes')} style={{ width: 90 }} /></div>
            <div style={{ flex: '1 1 200px' }}><div style={label}>Parameters (JSON)</div>
              <input id={id('pj')} value={String(d.ParamsJson ?? '')} onChange={set('ParamsJson')} placeholder='{"limit": 50}' style={{ width: '100%', fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }} /></div>
          </fieldset>
          {ready && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" disabled={!dirty || busy} onClick={() => onSave(d)}>Save schedule</button>
              {dirty && <button className="btn btn-secondary" disabled={busy} onClick={() => setD(initial)}>Discard</button>}
              <button className="btn btn-secondary" disabled={busy} onClick={onRun} style={{ marginLeft: 'auto' }}>
                <Play size={13} style={{ marginRight: 6 }} />Run now
              </button>
            </div>
          )}
        </div>
      )}

      <div style={label}>Recent runs</div>
      {runs.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>No runs in the last 3 days.</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {runs.slice(0, 10).map((r) => (
              <tr key={r.RunId}>
                <td style={{ ...td, padding: '4px 10px 4px 0', whiteSpace: 'nowrap' }}>{when(r.StartedAt)}</td>
                <td style={{ ...td, padding: '4px 10px' }}><RunStatus status={r.Status} /></td>
                <td style={{ ...td, padding: '4px 10px', whiteSpace: 'nowrap' }} className="muted">{r.TriggerKind === 'manual' ? `run now · ${r.TriggeredBy || ''}` : 'scheduled'}</td>
                <td style={{ ...td, padding: '4px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.DurationMs == null ? '' : `${(r.DurationMs / 1000).toFixed(1)}s`}</td>
                <td style={{ ...td, padding: '4px 10px' }}>{r.Error ? <span style={{ color: STATUS.critical }}>{r.Error}</span> : <Summary json={r.SummaryJson} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

type Tab = 'schedule' | 'runs' | 'settings' | 'intake' | 'audit';

export default function OperationsConsole() {
  const [data, setData] = useState<Overview | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [tab, setTab] = useState<Tab>('schedule');
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [settingDraft, setSettingDraft] = useState<Record<string, string>>({});

  const api = useCallback(async (qs: string, init?: RequestInit) => {
    const r = await fetch(`/api/ops-console${qs}`, { credentials: 'same-origin', cache: 'no-store', ...init });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.detail || body.error || `HTTP ${r.status}`);
    return body;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [o, r, a] = await Promise.all([api('?mode=overview'), api('?mode=runs&hours=72'), api('?mode=audit&days=90')]);
      setData(o); setRuns(r.runs || []); setAudit(a.audit || []);
    } catch (e) { setErr((e as Error).message); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);
  // The heartbeat is the thing most worth watching live; refresh quietly each minute.
  useEffect(() => { const t = setInterval(() => { api('?mode=overview').then(setData).catch(() => {}); }, 60000); return () => clearInterval(t); }, [api]);

  const act = useCallback(async (fn: () => Promise<Overview & { note?: string }>, okMsg: string) => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const o = await fn();
      setData(o); setNote(o.note || okMsg);
      const [r, a] = await Promise.all([api('?mode=runs&hours=72'), api('?mode=audit&days=90')]);
      setRuns(r.runs || []); setAudit(a.audit || []);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }, [api]);

  const patch = (body: object) => api('', { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

  const saveJob = (j: Job, d: Draft) => act(() => patch({ jobKey: j.JobKey, ...d }), `Saved. ${j.Title} follows the new schedule from the next heartbeat.`);
  const runJob = (j: Job) => act(() => api('?action=run', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ jobKey: j.JobKey }) }), `${j.Title} runs on the next heartbeat, within a minute.`);
  const saveSetting = (s: Setting) => {
    const v = settingDraft[s.SettingKey] ?? s.Value;
    if (s.SettingKey === 'memory.testRouteTo' && !v.trim()
      && !window.confirm('Clearing this ends the test round: real client and job partners become approvers and start getting emails. Continue?')) return;
    act(() => patch({ setting: s.SettingKey, value: v }), `Saved ${s.SettingKey}. Services pick it up within a minute.`)
      .then(() => setSettingDraft((x) => { const n = { ...x }; delete n[s.SettingKey]; return n; }));
  };
  const toggleIntake = (s: Space) => act(() => patch({ intakeUpn: s.Upn, enabled: !s.IntakeEnabled }), `${s.Upn}: intake ${s.IntakeEnabled ? 'off' : 'on'}.`);

  const jobs = data?.jobs || [];
  const beat = data?.heartbeats.find((h) => h.App === RUNNER_APP) || null;
  const byJob = useMemo(() => {
    const m: Record<string, Run[]> = {};
    for (const r of runs) (m[r.JobKey] ||= []).push(r);
    return m;
  }, [runs]);
  const ready = jobs.filter((j) => j.HandlerStatus === 'ready');
  const on = ready.filter((j) => j.Enabled);
  // Only a job that is ON can be failing now. A job switched off after failing (the old J1 refresh, retired when report builds moved to
  // func-shared-services) kept "2 failing" on the tile for days while nothing was running.
  const failing = on.filter((j) => j.LastStatus === 'failed' || j.LastStatus === 'timed-out');
  const nextUp = on.map((j) => j.Next.at).filter(Boolean).sort()[0] || null;
  const runs7 = jobs.reduce((s, j) => s + (j.Runs7d || 0), 0);
  const fails7 = jobs.reduce((s, j) => s + (j.Failures7d || 0), 0);
  const coverageJob = jobs.find((j) => j.JobKey === 'corpus.two-year-coverage') || null;
  const coverage = parse(coverageJob?.LastSummaryJson ?? null) as { percent: number | null; complete: number; projects: number } | null;

  return (
    <div style={{ padding: '18px 22px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Timer size={22} />
        <h1 style={{ margin: 0, fontSize: 22 }}>Operations</h1>
        <button className="btn" onClick={loadAll} disabled={loading} style={{ marginLeft: 'auto' }}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />{loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 14, maxWidth: 900 }}>
        Every scheduled refresh: what it writes, what triggers it, the rails that stop it doing damage, and its run history.
        A change you make here is the schedule. The function app reads these rows once a minute, so no deploy is needed.
        Failures go to the dev inbox and to the error log.
      </div>

      {/* Heartbeat: if this is red, nothing below is running. */}
      <div className="card" style={{ padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        borderLeft: `3px solid ${!beat ? STATUS.warning : beat.Stale ? STATUS.critical : STATUS.good}` }}>
        <HeartPulse size={16} style={{ color: !beat ? STATUS.warning : beat.Stale ? STATUS.critical : STATUS.good }} />
        {!beat && <span><strong>No heartbeat yet.</strong> <span className="muted">{RUNNER_APP} hasn't ticked since the scheduler was added. It starts once the deploy finishes.</span></span>}
        {beat && beat.Stale && <span style={{ color: STATUS.critical }}><strong>The scheduler has stopped.</strong> Last tick {ago(beat.LastTickAt)}. Nothing below is running. Check {RUNNER_APP} in the Azure portal.</span>}
        {beat && !beat.Stale && <span><strong>Scheduler running.</strong> <span className="muted">Last tick {ago(beat.LastTickAt)}{beat.LastTickMs != null ? ` (${beat.LastTickMs} ms)` : ''} · {RUNNER_APP} · once a minute</span></span>}
        {beat?.Uptime && (
          <span className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, color: beat.Uptime.percent < 99 ? STATUS.warning : undefined }}>
            Last 24 h: {beat.Uptime.percent}% of ticks ({beat.Uptime.ticks.toLocaleString()} of {beat.Uptime.expected.toLocaleString()})
            {' · '}longest gap {beat.Uptime.longestGapSeconds == null ? '—' : beat.Uptime.longestGapSeconds < 120 ? `${beat.Uptime.longestGapSeconds}s` : `${Math.round(beat.Uptime.longestGapSeconds / 60)} min`}
            {beat.Uptime.gaps > 0 && ` · ${beat.Uptime.gaps} missed`}
          </span>
        )}
      </div>

      {err && <div className="card" style={{ borderLeft: `3px solid ${STATUS.critical}`, padding: '10px 14px', marginBottom: 12, color: STATUS.critical }}>{err}</div>}
      {note && <div className="card" style={{ borderLeft: `3px solid ${STATUS.good}`, padding: '10px 14px', marginBottom: 12 }}>{note}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="Jobs on" value={`${on.length} of ${ready.length}`} sub={`${jobs.length - ready.length} more listed, not yet schedulable`} />
        <Tile label="Failing now" value={failing.length} accent={failing.length ? STATUS.critical : undefined} sub={failing.map((j) => j.Title).join(', ') || undefined} />
        <Tile label="Runs, last 7 days" value={runs7} sub={fails7 ? `${fails7} failed` : 'none failed'} accent={fails7 ? STATUS.warning : undefined} />
        <Tile label="Next run" value={nextUp ? until(nextUp) : '—'} sub={nextUp ? when(nextUp) : 'nothing switched on'} />
        {coverage && (
          <Tile label="Projects with two years on file" value={`${coverage.percent ?? '—'}%`}
            sub={`${coverage.complete} of ${coverage.projects} live projects · ${ago(coverageJob?.LastFinishedAt)}`}
            accent={Number(coverage.percent) < 90 ? STATUS.warning : STATUS.good} />
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {([
          ['schedule', <Timer size={14} key="i" />, 'Schedule'],
          ['runs', <History size={14} key="i" />, 'Run history'],
          ['settings', <Settings2 size={14} key="i" />, 'Settings'],
          ['intake', <Users size={14} key="i" />, 'Intake'],
          ['audit', <ScrollText size={14} key="i" />, 'Change log'],
        ] as [Tab, React.ReactNode, string][]).map(([k, icon, text]) => (
          <button key={k} className={tab === k ? 'btn' : 'btn btn-secondary'} onClick={() => setTab(k)}>
            {icon}<span style={{ marginLeft: 6 }}>{text}</span>
          </button>
        ))}
      </div>

      {tab === 'schedule' && (
        <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['', 'Refresh', 'Writes', 'Cadence', 'Freshness', 'State', 'Last run', 'Next'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {jobs.map((j) => {
                const isOpen = open === j.JobKey;
                const win = windowText(j);
                return (
                  <React.Fragment key={j.JobKey}>
                    <tr onClick={() => setOpen(isOpen ? null : j.JobKey)} style={{ cursor: 'pointer', borderTop: '1px solid var(--border)' }}>
                      <td style={{ ...td, width: 20 }}>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td style={{ ...td, minWidth: 190 }}>
                        <div style={{ fontWeight: 600 }}>{j.Title}</div>
                        <div className="muted" style={{ fontSize: 11 }}><code>{j.JobKey}</code> · {j.App}</div>
                      </td>
                      <td style={{ ...td, fontSize: 12, maxWidth: 260 }}>{j.Layer}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div>{cadenceText(j)}</div>
                        {win && <div className="muted" style={{ fontSize: 11 }}>{win}</div>}
                      </td>
                      <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap' }}>{j.FreshnessTarget}</td>
                      <td style={td}><StatePill j={j} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <RunStatus status={j.LastStatus} failures={j.ConsecutiveFailures > 1 ? j.ConsecutiveFailures : 0} />
                        {j.LastStartedAt && <div className="muted" style={{ fontSize: 11 }}>{ago(j.LastStartedAt)}{!j.Enabled && j.LastStatus !== 'succeeded' ? ' · off since' : ''}</div>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>
                        {j.Next.at ? <><div>{until(j.Next.at)}</div><div className="muted" style={{ fontSize: 11 }}>{when(j.Next.at)}</div></>
                          : <span className="muted">{j.Next.why}</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr><td colSpan={8} style={{ padding: 0 }}>
                        <JobDetail j={j} runs={byJob[j.JobKey] || []} busy={busy} onSave={(d) => saveJob(j, d)} onRun={() => runJob(j)} />
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'runs' && (
        <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
          {runs.length === 0 ? <div className="muted" style={{ padding: 14 }}>No runs in the last 3 days.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Started', 'Job', 'Result', 'Trigger', 'Took', 'Items', 'Detail'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.RunId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(r.StartedAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{jobs.find((j) => j.JobKey === r.JobKey)?.Title || r.JobKey}</td>
                    <td style={td}><RunStatus status={r.Status} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} className="muted">{r.TriggerKind === 'manual' ? `run now · ${r.TriggeredBy || ''}` : 'scheduled'}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.DurationMs == null ? '' : `${(r.DurationMs / 1000).toFixed(1)}s`}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.ItemsProcessed ?? ''}</td>
                    <td style={td}>{r.Error ? <span style={{ color: STATUS.critical, fontSize: 12 }}>{r.Error}</span> : <Summary json={r.SummaryJson} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 12.5, padding: '10px 14px' }}>
            Read by the memory service and the scheduler; changes take effect within a minute. <strong>memory.testRouteTo</strong> is the test-round switch: while it's set, that person is the only approver and gets every partner notification.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Setting', 'Value', 'What it does', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(data?.settings || []).map((s) => {
                const v = settingDraft[s.SettingKey] ?? s.Value;
                const dirty = v !== s.Value;
                return (
                  <tr key={s.SettingKey} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <code style={{ fontSize: 12 }}>{s.SettingKey}</code>
                      {s.SettingKey === 'memory.testRouteTo' && s.Value && <div style={{ fontSize: 11, color: STATUS.warning, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><AlertTriangle size={11} />test round on</div>}
                    </td>
                    <td style={{ ...td, minWidth: 260 }}>
                      <input id={`ops-setting-${s.SettingKey}`} value={v} disabled={busy}
                        onChange={(e) => setSettingDraft({ ...settingDraft, [s.SettingKey]: e.target.value })}
                        style={{ width: '100%', fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }} />
                      {s.UpdatedBy && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>changed {ago(s.UpdatedAt)} by {s.UpdatedBy}</div>}
                    </td>
                    <td style={{ ...td, fontSize: 12, maxWidth: 420 }} className="muted">{s.Description}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dirty && <button className="btn" disabled={busy} onClick={() => saveSetting(s)}>Save</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'intake' && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 12.5, padding: '10px 14px' }}>
            Who the <strong>Proposal intake</strong> job scans. When it's on for a person, files they save to <code>BMSS AI/_Promote</code> become proposals without them clicking "Check my _Promote folder". The job itself also has to be on, in Schedule.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Person', 'Proposals so far', 'Intake', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(data?.personalSpaces || []).map((s) => (
                <tr key={s.Upn} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}>{s.Upn}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{s.Proposals}</td>
                  <td style={td}>{s.IntakeEnabled ? <span style={{ color: STATUS.good }}>scanned</span> : <span className="muted">not scanned</span>}</td>
                  <td style={td}><button className="btn btn-secondary" disabled={busy} onClick={() => toggleIntake(s)}>{s.IntakeEnabled ? 'Stop scanning' : 'Start scanning'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audit' && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          {audit.length === 0 ? <div className="muted" style={{ padding: 14 }}>No changes in the last 90 days.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['When', 'Who', 'What', 'Field', 'From', 'To'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.AuditId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(a.ChangedAt)}</td>
                    <td style={td}>{a.ChangedBy}</td>
                    <td style={td}><span className="muted" style={{ fontSize: 11 }}>{a.Entity}</span> <code style={{ fontSize: 12 }}>{a.EntityKey}</code></td>
                    <td style={td}>{a.Field}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }} className="muted">{a.OldValue ?? '∅'}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }}>{a.NewValue ?? '∅'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
